import { useEffect, useState, useCallback } from "react";
import { api, onAuthError, ApiError } from "./api";
import type { MeResponse } from "@shared/types";
import { AuthContext } from "./auth/AuthContext";
import { LoginScreen } from "./auth/LoginScreen";
import { ChangePasswordScreen } from "./auth/ChangePasswordScreen";
import { CrmShell } from "./crm/CrmShell";

type AppState =
  | { kind: "loading" }
  | { kind: "loginRequired" }
  | { kind: "mustChangePassword"; user: MeResponse }
  | { kind: "ready"; user: MeResponse };

export function App() {
  const [state, setState] = useState<AppState>({ kind: "loading" });

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      if (!me) { setState({ kind: "loginRequired" }); return; }
      if (me.must_change_password) setState({ kind: "mustChangePassword", user: me });
      else setState({ kind: "ready", user: me });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setState({ kind: "loginRequired" });
      else throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch {}
    localStorage.removeItem("crm_ss_cache_v1");
    setState({ kind: "loginRequired" });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const unsub = onAuthError(() => setState({ kind: "loginRequired" }));
    return unsub;
  }, []);

  if (state.kind === "loading") return <div style={{ padding: 40, textAlign: "center" }}>Carregando…</div>;
  if (state.kind === "loginRequired") return <LoginScreen onSuccess={refresh} />;

  return (
    <AuthContext.Provider value={{ user: state.user, refresh, logout }}>
      {state.kind === "mustChangePassword"
        ? <ChangePasswordScreen requireCurrent={false} onSuccess={refresh} />
        : <CrmShell />}
    </AuthContext.Provider>
  );
}
